import axios from "axios";
import { z } from "zod";

import { clearAuthSession, getAuthToken, type StoredAuthUser } from "./auth-session";

export function getAuthBaseUrl(): string {
  return (process.env.NEXT_PUBLIC_BFF_API_BASE_URL ?? "").replace(/\/$/, "");
}

export function getBffApiKey(): string {
  return (
    process.env.NEXT_PUBLIC_AUNIX_API_KEY ||
    process.env.NEXT_PUBLIC_BFF_API_KEY ||
    ""
  ).trim();
}

export const bffClient = axios.create({
  baseURL: getAuthBaseUrl(),
  headers: { "Content-Type": "application/json" },
  timeout: 20_000,
});

bffClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  const apiKey = getBffApiKey();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (apiKey) config.headers["x-api-key"] = apiKey;
  return config;
});

bffClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const requestUrl = String(error.config?.url ?? "");
    if (status === 401 && !requestUrl.includes("/auth/login") && typeof window !== "undefined") {
      clearAuthSession();
      if (!window.location.pathname.startsWith("/login")) {
        const from = `${window.location.pathname}${window.location.search}`;
        window.location.href = `/login?from=${encodeURIComponent(from)}`;
      }
    }
    return Promise.reject(error);
  },
);

const LoginResponseSchema = z.object({
  jwtToken: z.object({ accessToken: z.string() }),
  user: z.object({
    _id: z.coerce.string(),
    name: z.string(),
    email: z.string(),
    role: z.string().optional(),
    tenantId: z.coerce.string().optional(),
  }),
  tenant: z
    .object({
      _id: z.coerce.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
});

export type LoginResult = {
  accessToken: string;
  user: StoredAuthUser;
};

function loginErrorMessage(error: unknown): string {
  if (typeof error === "object" && error && "response" in error) {
    const response = (error as { response?: { status?: number; data?: unknown } }).response;
    const data = response?.data;
    const serverMessage =
      (typeof data === "object" &&
        data &&
        "message" in data &&
        typeof data.message === "string" &&
        data.message) ||
      (typeof data === "object" &&
        data &&
        "error" in data &&
        typeof data.error === "string" &&
        data.error) ||
      null;
    if (response?.status === 401) {
      return serverMessage || "Invalid credentials. Please check your email and password.";
    }
    if (serverMessage) return serverMessage;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Login failed. Please try again later.";
}

export async function loginWithPassword(email: string, password: string): Promise<LoginResult> {
  if (!getBffApiKey()) {
    throw new Error("BFF API key is not configured (NEXT_PUBLIC_AUNIX_API_KEY).");
  }

  try {
    const response = await bffClient.post("/auth/login", {
      username: email.trim(),
      password,
    });
    const parsed = LoginResponseSchema.parse(response.data);
    return {
      accessToken: parsed.jwtToken.accessToken,
      user: {
        userId: parsed.user._id,
        name: parsed.user.name,
        email: parsed.user.email,
        role: parsed.user.role,
        tenantId: parsed.user.tenantId ?? parsed.tenant?._id,
        tenantName: parsed.tenant?.name,
      },
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error("Login succeeded but the server returned an unexpected shape.");
    }
    throw new Error(loginErrorMessage(error));
  }
}

export async function logoutFromBff(): Promise<void> {
  try {
    await bffClient.post("/auth/logout", {});
  } catch {
    // Always clear local session even if BFF logout fails.
  }
}
